import { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE, FEATURES, SEARCH_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { decodeUriComponent } from '@soundxyz/utils/decodeUri';
import { useParams } from '@context/params';
import type { SearchQueryVariables } from '@graphql/reactQuery';
import { SearchDocument, useInfiniteQuery } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { getSortOrder } from '@utils/helpers';
import { sortFieldsInput } from '@utils/sort';
import { isAvailabilityType } from './helpers';
import { EXPLORE_TYPES, SEARCH_TAB_CHOICES } from './schema';

/*#__PURE__*/ gql(/* GraphQL */ `
  query InitialSearchResults(
    $releaseFilter: ReleasesCursorFilterArgs
    $artistFilter: ArtistCursorFilterArgs
    $playlistFilter: AllShelvesFilter
    $collectorFilter: AllCollectorsFilter
  ) {
    releases(pagination: { first: 20, sort: { createdAt: DESC } }, filter: $releaseFilter) {
      edges {
        node {
          id
          __typename
          track {
            id
          }
          ...SearchResultRelease
        }
      }
    }
    artists(pagination: { first: 20, sort: DESC }, filter: $artistFilter) {
      edges {
        node {
          id
          __typename
          ...SearchResultArtist
        }
      }
    }
    allCollectors(
      input: { filter: $collectorFilter, pagination: { first: 20, sort: { createdAt: DESC } } }
    ) {
      edges {
        node {
          id
          __typename
          ...SearchResultCollector
        }
      }
    }
    allShelves(
      input: { filter: $playlistFilter, pagination: { first: 20, sort: { createdAt: DESC } } }
    ) {
      edges {
        node {
          id
          __typename
          releaseCount
          ...PlaylistCardDetails
        }
      }
    }
  }

  query Search(
    $input: SearchInput!
    $pagination: SearchConnectionArgs
    $hasReleases: Boolean!
    $hasArtists: Boolean!
    $hasCollectors: Boolean!
    $hasPlaylists: Boolean!
    $releaseFilter: ReleaseSearchFilter
    $artistFilter: ArtistSearchFilter
    $playlistFilter: ShelfSearchFilter
    $collectorFilter: CollectorSearchFilter
    $releaseSort: [ReleaseSortInput!]
    $artistSort: [ArtistSortInput!]
    $collectorSort: [CollectorSortInput!]
    $playlistSort: [ShelfSortInput!]
  ) {
    search(input: $input) {
      id
      releasesPaginated(pagination: $pagination, filter: $releaseFilter, sortFields: $releaseSort)
        @include(if: $hasReleases) {
        edges {
          node {
            __typename
            track {
              id
            }
            id
            type
            ...SearchResultRelease
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      artistsPaginated(pagination: $pagination, filter: $artistFilter, sortFields: $artistSort)
        @include(if: $hasArtists) {
        edges {
          node {
            __typename
            id
            ...SearchResultArtist
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      collectors(pagination: $pagination, filter: $collectorFilter, sortFields: $collectorSort)
        @include(if: $hasCollectors) {
        edges {
          node {
            __typename
            id
            ...SearchResultCollector
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      shelves(pagination: $pagination, filter: $playlistFilter, sortFields: $playlistSort)
        @include(if: $hasPlaylists) {
        edges {
          node {
            __typename
            id
            releaseCount
            ...PlaylistCardDetails
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`);

export const INITIAL_PAGE_SIZE = 20;

const EDGE_TYPES = {
  RELEASES: 'releasesPaginated',
  ARTISTS: 'artistsPaginated',
  COLLECTORS: 'collectors',
  PLAYLISTS: 'shelves',
} as const;

export function useSearchResults() {
  const logError = useLogError();
  const {
    q,
    tab,
    genre,
    search_location_place_id,
    search_availability,
    explore_type,
    mint_start_time,
    total_minted,
    created_at,
    num_referral_purchases,
    total_volume,
    artists_backed,
  } = useParams();
  const decodedQuery = decodeUriComponent(q || '');
  const decodedTab = decodeUriComponent(tab || '');
  const decodedSearchLocationPlaceId = decodeUriComponent(search_location_place_id || '');
  const decodedSearchAvailability = decodeUriComponent(search_availability || '');
  const decodedExploreType = decodeUriComponent(explore_type || '');

  const hasReleases = decodedExploreType
    ? [EXPLORE_TYPES.SOUNDS].includes(decodedExploreType)
    : [SEARCH_TAB_CHOICES.SOUNDS, SEARCH_TAB_CHOICES.ALL].includes(decodedTab) || !decodedTab;
  const hasArtists = decodedExploreType
    ? [EXPLORE_TYPES.ARTISTS].includes(decodedExploreType)
    : [SEARCH_TAB_CHOICES.ARTISTS, SEARCH_TAB_CHOICES.ALL].includes(decodedTab);
  const hasCollectors = decodedExploreType
    ? [EXPLORE_TYPES.COLLECTORS].includes(decodedExploreType)
    : [SEARCH_TAB_CHOICES.COLLECTORS, SEARCH_TAB_CHOICES.ALL].includes(decodedTab);
  const hasPlaylists = decodedExploreType
    ? [EXPLORE_TYPES.PLAYLISTS].includes(decodedExploreType)
    : [SEARCH_TAB_CHOICES.PLAYLISTS, SEARCH_TAB_CHOICES.ALL].includes(decodedTab);

  const edgeType = useMemo((): (typeof EDGE_TYPES)[keyof typeof EDGE_TYPES] | undefined => {
    // If all is selected, we don't paginate
    if (decodedTab === SEARCH_TAB_CHOICES.ALL) {
      return undefined;
    }

    if (hasReleases) {
      return EDGE_TYPES.RELEASES;
    }

    if (hasArtists) {
      return EDGE_TYPES.ARTISTS;
    }

    if (hasCollectors) {
      return EDGE_TYPES.COLLECTORS;
    }

    if (hasPlaylists) {
      return EDGE_TYPES.PLAYLISTS;
    }

    return undefined;
  }, [decodedTab, hasReleases, hasArtists, hasCollectors, hasPlaylists]);

  const filters = {
    releaseFilter: hasReleases
      ? {
          genres: genre ? [genre] : null,
          locationIds: decodedSearchLocationPlaceId ? [decodedSearchLocationPlaceId] : null,
          releaseStatus: isAvailabilityType(decodedSearchAvailability)
            ? [decodedSearchAvailability]
            : null,
        }
      : null,
    artistFilter: hasArtists
      ? {
          genres: genre ? [genre] : null,
          locationIds: decodedSearchLocationPlaceId ? [decodedSearchLocationPlaceId] : null,
        }
      : null,
    playlistFilter: hasPlaylists ? { genres: genre ? [genre] : null } : null,
    collectorFilter: hasCollectors
      ? { locationIds: decodedSearchLocationPlaceId ? [decodedSearchLocationPlaceId] : null }
      : null,
    input: {
      text: decodedQuery || null,
    },
    hasReleases,
    hasArtists,
    hasCollectors,
    hasPlaylists,
  } satisfies Partial<SearchQueryVariables>;

  const totalVolumeSort = getSortOrder({ sortType: total_volume });
  const mintStartTimeSort = getSortOrder({ sortType: mint_start_time });
  const totalMintedSort = getSortOrder({ sortType: total_minted });
  const createdAtSort = getSortOrder({ sortType: created_at });
  const artistsBackedSort = getSortOrder({ sortType: artists_backed });
  const numReferralPurchasesSort = getSortOrder({ sortType: num_referral_purchases });

  const sort = {
    releaseSort: hasReleases
      ? sortFieldsInput(
          {
            field: 'TOTAL_VOLUME',
            order: totalMintedSort,
          },
          {
            field: 'MINT_START_TIME',
            order: mintStartTimeSort,
          },
          { field: 'TOTAL_MINTED', order: totalMintedSort },
        )
      : undefined,

    artistSort: hasArtists
      ? sortFieldsInput(
          {
            field: 'TOTAL_VOLUME',
            order: totalVolumeSort,
          },
          {
            field: 'CREATED_AT',
            order: createdAtSort,
          },
          {
            field: 'TOTAL_MINTED',
            order: totalMintedSort,
          },
        )
      : undefined,
    collectorSort: hasCollectors
      ? sortFieldsInput(
          {
            field: 'TOTAL_VOLUME',
            order: totalVolumeSort,
          },
          {
            field: 'TOTAL_MINTED',
            order: totalMintedSort,
          },
          {
            field: 'ARTISTS_BACKED',
            order: artistsBackedSort,
          },
        )
      : undefined,
    playlistSort: hasPlaylists
      ? sortFieldsInput(
          {
            field: 'CREATED_AT',
            order: createdAtSort,
          },
          {
            field: 'NUM_REFERRAL_PURCHASES',
            order: numReferralPurchasesSort,
          },
        )
      : undefined,
    input: {
      text: decodedQuery || null,
    },
    hasReleases,
    hasArtists,
    hasCollectors,
    hasPlaylists,
  } satisfies Partial<SearchQueryVariables>;

  const {
    orderedList: searchResults,
    loadMoreNextPage: loadMore,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage = false,
  } = useInfiniteQuery(SearchDocument, {
    filterQueryKey: {
      filters,
      sort,
    },
    variables({ pageParam }) {
      return {
        pagination: {
          after: pageParam?.after || null,
          first: INITIAL_PAGE_SIZE,
        },
        ...filters,
        ...sort,
      } satisfies SearchQueryVariables;
    },
    getNextPageParam({ data }) {
      return edgeType
        ? data.search[edgeType]?.pageInfo.hasNextPage && {
            after: data.search[edgeType]?.pageInfo.endCursor,
          }
        : undefined;
    },
    list({ search }) {
      if (!edgeType) return [];
      return search[edgeType]?.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
    onError(error) {
      const errorMessage = 'Error while fetching search results';
      logError({
        action: SEARCH_ERROR_ACTIONS.SEARCH_RESULTS_FETCH_ERROR,
        error,
        closeProcessingModal: true,
        message: errorMessage,
        errorType: ERROR_TYPE.FETCH_SEARCH_RESULTS_FAILED,
        feature: FEATURES.SEARCH,
        isArtistOnlyPage: false,
        level: 'fatal',
        toast: errorMessage,
      });
    },
  });

  return {
    searchResults,
    loadMore,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
  };
}
